// Product editor (PRODUCT-01/03/04/10/11, IA §2.1 …/products/new and …/products/{id}). One form,
// progressive sections, a single column at 360 px and main + side columns from 1024 px:
//  - options (≤ 3 axes) → the combination matrix; unticked combinations are never created (sparse);
//  - price, compare-at, cost (only with products.cost.read), SKU, barcode, weight per variant;
//  - photos (browser renditions, MEDIA-02), personalisation fields with a per-item surcharge that the
//    server applies at checkout (PRODUCT-04), visibility (status, channels, buy button), taxonomy, SEO.
// Edits send If-Match; a 412 shows the conflict banner with Reload (no silent overwrite).
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import {
  Banner,
  Button,
  Checkbox,
  cx,
  Dialog,
  EmptyState,
  Field,
  Input,
  MoneyInput,
  Select,
  SkeletonText,
  StatePanel,
  Switch,
  useToast,
} from "@ady/ui";
import { ApiError } from "../api/client";
import { useIdempotencyKeys } from "../api/idempotency";
import { uploadPhoto, UploadRejected } from "../media/upload";
import { useGo } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import {
  catalogApi,
  catalogKeys,
  useBrands,
  useCategories,
  useMediaLibrary,
  type Axis,
  type Channel,
  type CustomField,
  type FieldType,
  type Product,
  type ProductStatus,
  type PurchasableMode,
  type VariantInput,
} from "./api";
import {
  CTID,
  Card,
  CatalogPage,
  LoadError,
  Textarea,
  catalogError,
  splitList,
  useStoreIds,
} from "./ui";

// ---------------------------------------------------------------- pure helpers (exported for tests)

export function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[2-6]|li|blockquote)>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export function textToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${p.split("\n").map(esc).join("<br>")}</p>`)
    .join("");
}

export interface AxisDraft {
  name: string;
  values: string;
}
export const axesOf = (drafts: AxisDraft[]): Axis[] =>
  drafts
    .map((a) => ({ name: a.name.trim(), values: splitList(a.values) }))
    .filter((a) => a.name && a.values.length);

export function combinations(axes: Axis[]): Record<string, string>[] {
  let out: Record<string, string>[] = [{}];
  for (const a of axes) out = out.flatMap((o) => a.values.map((v) => ({ ...o, [a.name]: v })));
  return out;
}
export const keyOf = (axes: Axis[], o: Record<string, string>) =>
  axes.map((a) => `${a.name}=${o[a.name] ?? ""}`).join("|");
const titleOf = (axes: Axis[], o: Record<string, string>) =>
  axes.length ? axes.map((a) => o[a.name]).join(" / ") : t("catalog.variants.default");

interface RowDraft {
  selected: boolean;
  price: number | null;
  compareAt: number | null;
  cost: number | null;
  sku: string;
  barcode: string;
  weight: string;
  id?: string;
  version?: number;
}
const emptyRow = (price: number | null = null): RowDraft => ({
  selected: true,
  price,
  compareAt: null,
  cost: null,
  sku: "",
  barcode: "",
  weight: "",
});

interface FieldDraft {
  id?: string;
  type: FieldType;
  label: string;
  required: boolean;
  surcharge: number | null;
  choices: string;
}

interface Draft {
  title: string;
  description: string;
  status: ProductStatus;
  channels: Channel[];
  purchasable: PurchasableMode;
  externalUrl: string;
  categoryIds: string[];
  brandId: string;
  tags: string;
  track: boolean;
  backorder: boolean;
  tax: "standard" | "exempt" | "zero";
  seoTitle: string;
  seoDescription: string;
  media: { asset_id: string; alt: string; preview_path: string | null }[];
  axes: AxisDraft[];
  rows: Record<string, RowDraft>;
  fields: FieldDraft[];
}

const blank = (): Draft => ({
  title: "",
  description: "",
  status: "draft",
  channels: ["web", "pos"],
  purchasable: "buy",
  externalUrl: "",
  categoryIds: [],
  brandId: "",
  tags: "",
  track: true,
  backorder: false,
  tax: "standard",
  seoTitle: "",
  seoDescription: "",
  media: [],
  axes: [],
  rows: { "": emptyRow() },
  fields: [],
});

function draftOf(p: Product): Draft {
  const rows: Record<string, RowDraft> = {};
  for (const v of p.variants) {
    rows[keyOf(p.options, v.options)] = {
      selected: true,
      price: v.price_paisa,
      compareAt: v.compare_at_paisa,
      cost: v.cost_paisa ?? null,
      sku: v.sku ?? "",
      barcode: v.barcode ?? "",
      weight: v.weight_g === null ? "" : String(v.weight_g),
      id: v.id,
      version: v.version,
    };
  }
  return {
    title: p.title,
    description: htmlToText(p.description_html),
    status: p.status,
    channels: p.channels,
    purchasable: p.purchasable_mode,
    externalUrl: p.external_url ?? "",
    categoryIds: p.category_ids,
    brandId: p.brand_id ?? "",
    tags: p.tags.join(", "),
    track: p.track_inventory,
    backorder: p.allow_backorder,
    tax: p.tax_class,
    seoTitle: p.seo.title ?? "",
    seoDescription: p.seo.description ?? "",
    media: p.media,
    axes: p.options.map((a) => ({ name: a.name, values: a.values.join(", ") })),
    rows,
    fields: p.custom_fields.map((f) => ({
      ...(f.id ? { id: f.id } : {}),
      type: f.type,
      label: f.label,
      required: f.required,
      surcharge: f.surcharge_paisa,
      choices: f.options.map((o) => o.value).join(", "),
    })),
  };
}

const fieldsOut = (fields: FieldDraft[]): CustomField[] =>
  fields
    .filter((f) => f.label.trim())
    .map((f) => ({
      ...(f.id ? { id: f.id } : {}),
      type: f.type,
      label: f.label.trim(),
      required: f.required,
      options:
        f.type === "select" || f.type === "multi"
          ? splitList(f.choices).map((value) => ({ value }))
          : [],
      surcharge_paisa: f.surcharge ?? 0,
      config: f.type === "slot" ? { slots: splitList(f.choices) } : {},
    }));

// ---------------------------------------------------------------- page

export function ProductEditorPage() {
  const params = useParams({ strict: false }) as { productId?: string };
  const productId = params.productId;
  const { orgId, storeId } = useStoreIds();
  const product = useQuery({
    queryKey: catalogKeys.product(orgId, storeId, productId ?? "new"),
    queryFn: () => catalogApi.product(orgId, storeId, productId!),
    enabled: !!productId,
  });
  if (!productId) return <Editor key="new" initial={null} />;
  if (product.isPending)
    return (
      <div data-testid={CTID.loadingState} className="max-w-4xl">
        <SkeletonText lines={8} />
      </div>
    );
  if (product.isError) {
    if (product.error instanceof ApiError && product.error.status === 404)
      return (
        <StatePanel
          kind="error"
          size="page"
          title={t("catalog.editor.notFound")}
          data-testid={CTID.editorPage}
        />
      );
    return <LoadError error={product.error} onRetry={() => void product.refetch()} />;
  }
  return <Editor key={`${product.data.id}:${product.data.version}`} initial={product.data} />;
}

function Editor({ initial }: { initial: Product | null }) {
  const { membership, orgSlug, storeSlug } = useShell();
  const { orgId, storeId } = useStoreIds();
  const qc = useQueryClient();
  const toast = useToast();
  const go = useGo();
  const keyFor = useIdempotencyKeys();
  const canWrite = can(membership, ["products.write"]);
  const canDelete = can(membership, ["products.delete"]);
  const canCost = can(membership, ["products.cost.read"]);
  const [d, setD] = useState<Draft>(() => (initial ? draftOf(initial) : blank()));
  const [errors, setErrors] = useState<{ title?: string; variants?: string; channels?: string }>(
    {},
  );
  const [formError, setFormError] = useState<string>();
  const [conflict, setConflict] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const categories = useCategories(orgId, storeId);
  const brands = useBrands(orgId, storeId);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));

  const axes = useMemo(() => axesOf(d.axes), [d.axes]);
  const combos = useMemo(() => combinations(axes), [axes]);
  // Sparse matrix (PRODUCT-11): on an existing product, a combination that was never created stays
  // unticked unless it uses an option or value the merchant has just added in this edit.
  const known = useMemo(() => {
    const s = new Set<string>();
    for (const a of initial?.options ?? []) for (const v of a.values) s.add(`${a.name}=${v}`);
    return s;
  }, [initial]);
  const row = (k: string): RowDraft => {
    const existing = d.rows[k];
    if (existing) return existing;
    const fresh = emptyRow(d.rows[Object.keys(d.rows)[0] ?? ""]?.price ?? null);
    if (initial) fresh.selected = k.split("|").some((pair) => pair && !known.has(pair));
    return fresh;
  };
  const setRow = (k: string, patch: Partial<RowDraft>) =>
    setD((x) => ({ ...x, rows: { ...x.rows, [k]: { ...(x.rows[k] ?? row(k)), ...patch } } }));
  const selected = combos.filter((o) => row(keyOf(axes, o)).selected);

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: catalogKeys.productsAll(orgId, storeId) }),
      qc.invalidateQueries({ queryKey: ["org", orgId, "store", storeId, "product"] }),
    ]);

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!d.title.trim()) next.title = t("catalog.editor.titleRequired");
    if (selected.length === 0) next.variants = t("catalog.variants.noneSelected");
    else if (selected.some((o) => row(keyOf(axes, o)).price === null))
      next.variants = t("catalog.variants.priceRequired");
    if (d.channels.length === 0) next.channels = t("catalog.editor.channelsNone");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const variantOf = (o: Record<string, string>): VariantInput => {
    const r = row(keyOf(axes, o));
    return {
      options: o,
      price_paisa: r.price ?? 0,
      compare_at_paisa: r.compareAt,
      ...(canCost ? { cost_paisa: r.cost } : {}),
      sku: r.sku.trim() || null,
      barcode: r.barcode.trim() || null,
      weight_g: r.weight.trim() ? Math.round(Number(r.weight)) : null,
    };
  };

  const common = () => ({
    title: d.title.trim(),
    description_html: textToHtml(d.description),
    status: d.status,
    channels: d.channels,
    purchasable_mode: d.purchasable,
    external_url: d.purchasable === "external" ? d.externalUrl.trim() || null : null,
    brand_id: d.brandId || null,
    category_ids: d.categoryIds,
    tags: splitList(d.tags),
    track_inventory: d.track,
    allow_backorder: d.backorder,
    tax_class: d.tax,
    seo: { title: d.seoTitle.trim(), description: d.seoDescription.trim() },
    media: d.media.map((m) => ({ asset_id: m.asset_id, alt: m.alt })),
  });

  const save = useMutation({
    mutationFn: async (): Promise<Product> => {
      if (!initial) {
        const body = {
          ...common(),
          options: axes,
          variants: selected.map(variantOf),
          custom_fields: fieldsOut(d.fields),
        };
        return catalogApi.createProduct(orgId, storeId, body, keyFor(body));
      }
      let p = await catalogApi.updateProduct(orgId, storeId, initial.id, common(), initial.version);
      const wantKeys = selected.map((o) => keyOf(axes, o)).sort();
      const haveKeys = p.variants.map((v) => keyOf(p.options, v.options)).sort();
      if (
        JSON.stringify(axes) !== JSON.stringify(p.options) ||
        JSON.stringify(wantKeys) !== JSON.stringify(haveKeys)
      ) {
        const first = selected[0] ? row(keyOf(axes, selected[0])).price : null;
        p = await catalogApi.generateVariants(
          orgId,
          storeId,
          p.id,
          {
            axes,
            selected_combinations: selected,
            ...(first !== null ? { defaults: { price_paisa: first } } : {}),
          },
          p.version,
        );
      }
      let version = p.version;
      for (const v of p.variants) {
        const want = variantOf(v.options);
        const changed =
          want.price_paisa !== v.price_paisa ||
          (want.compare_at_paisa ?? null) !== v.compare_at_paisa ||
          (canCost && (want.cost_paisa ?? null) !== (v.cost_paisa ?? null)) ||
          (want.sku ?? null) !== v.sku ||
          (want.barcode ?? null) !== v.barcode ||
          (want.weight_g ?? null) !== v.weight_g;
        if (!changed) continue;
        const { options: _o, ...patch } = want;
        void _o;
        const res = await catalogApi.updateVariant(orgId, storeId, v.id, patch, v.version);
        version = res.product_version;
      }
      const fields = fieldsOut(d.fields);
      if (JSON.stringify(fields) !== JSON.stringify(fieldsOut(draftOf(initial).fields))) {
        version = (await catalogApi.putCustomFields(orgId, storeId, p.id, fields, version)).version;
      }
      return version === p.version ? p : catalogApi.product(orgId, storeId, p.id);
    },
    onSuccess: async (p) => {
      toast.show({
        tone: "success",
        title: initial ? t("catalog.editor.saved") : t("catalog.editor.created"),
      });
      await invalidate();
      if (!initial) go(`/${orgSlug}/${storeSlug}/products/${p.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "precondition.version_mismatch") setConflict(true);
      else setFormError(catalogError(e));
    },
  });

  const remove = useMutation({
    mutationFn: () => catalogApi.deleteProduct(orgId, storeId, initial!.id),
    onSuccess: async () => {
      toast.show({ tone: "success", title: t("catalog.editor.deleted") });
      await invalidate();
      go(`/${orgSlug}/${storeSlug}/products`);
    },
    onError: (e) => toast.show({ tone: "error", title: catalogError(e) }),
  });
  const duplicate = useMutation({
    mutationFn: () => catalogApi.duplicate(orgId, storeId, initial!.id),
    onSuccess: async (p) => {
      toast.show({ tone: "success", title: t("catalog.editor.duplicated") });
      await invalidate();
      go(`/${orgSlug}/${storeSlug}/products/${p.id}`);
    },
    onError: (e) => toast.show({ tone: "error", title: catalogError(e) }),
  });

  // D-038 layout: three steps over one form. Every section stays rendered (one save covers all);
  // picking a step scrolls to it.
  const [stepAt, setStepAt] = useState(1);
  const goStep = (n: number) => {
    setStepAt(n);
    document
      .getElementById(`product-step-${n}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // The list's row menu links to a part of the editor (#basics, #variants, #personalization, #seo).
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const step = {
      basics: 1,
      visibility: 1,
      organization: 1,
      media: 1,
      variants: 2,
      shipping: 2,
      personalization: 3,
      seo: 3,
    }[id];
    if (step) setStepAt(step);
    const h = setTimeout(
      // A section card carries its id on its heading (`<id>-h`).
      () =>
        (document.getElementById(id) ?? document.getElementById(`${id}-h`))?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      150,
    );
    return () => clearTimeout(h);
  }, []);

  const submit = () => {
    setFormError(undefined);
    if (!validate()) return;
    save.mutate();
  };

  return (
    <CatalogPage
      title={initial ? t("catalog.editor.editTitle") : t("catalog.editor.newTitle")}
      anyOf={["products.read"]}
      testId={CTID.editorPage}
      canonical="PRODUCT-001"
      wide
      actions={
        <>
          <Button variant="ghost" onClick={() => go(`/${orgSlug}/${storeSlug}/products`)}>
            {t("catalog.editor.back")}
          </Button>
          {initial && canWrite ? (
            <Button
              variant="secondary"
              loading={duplicate.isPending}
              onClick={() => duplicate.mutate()}
              data-testid={CTID.editorDuplicate}
            >
              {t("catalog.editor.duplicate")}
            </Button>
          ) : null}
          {initial && canDelete ? (
            <Button
              variant="secondary"
              onClick={() => setDeleting(true)}
              data-testid={CTID.editorDelete}
            >
              {t("catalog.editor.delete")}
            </Button>
          ) : null}
          {canWrite ? (
            <Button
              icon="check"
              loading={save.isPending}
              loadingLabel={t("catalog.editor.saving")}
              onClick={submit}
              data-testid={CTID.editorSave}
            >
              {t("catalog.editor.save")}
            </Button>
          ) : null}
        </>
      }
    >
      {!canWrite ? <Banner kind="permission-denied" title={t("catalog.editor.readOnly")} /> : null}
      {conflict ? (
        <Banner
          kind="error"
          title={t("catalog.editor.conflict")}
          action={{ label: t("catalog.editor.reload"), onClick: () => void invalidate() }}
        />
      ) : null}
      {formError ? <Banner kind="error" title={formError} /> : null}
      <ProductSteps current={stepAt} onPick={goStep} />
      <fieldset disabled={!canWrite} className="flex max-w-4xl min-w-0 flex-col gap-6">
        <section
          id="product-step-1"
          aria-labelledby="product-step-1-h"
          className="flex scroll-mt-24 flex-col gap-4"
        >
          <h2 id="product-step-1-h" className="ady-sr-only">
            {t("catalog.editor.steps.general")}
          </h2>
          <Card title={t("catalog.editor.sections.basics")} id="basics">
            <Field label={t("catalog.editor.titleLabel")} error={errors.title} required>
              <Input
                value={d.title}
                onChange={(e) => set("title", e.target.value)}
                userContent
                maxGraphemes={200}
                data-testid={CTID.editorTitle}
              />
            </Field>
            <Field
              label={t("catalog.editor.descriptionLabel")}
              help={t("catalog.editor.descriptionHelp")}
              optional
            >
              <Textarea
                value={d.description}
                onChange={(e) => set("description", e.target.value)}
                rows={6}
              />
            </Field>
          </Card>
          <Card title={t("catalog.editor.sections.visibility")} id="visibility">
            <Field
              label={t("catalog.editor.statusLabel")}
              help={t(`catalog.editor.statusHelp.${d.status}`)}
            >
              <Select
                value={d.status}
                onChange={(e) => set("status", e.target.value as ProductStatus)}
                options={(["draft", "active", "unlisted", "archived"] as const).map((s) => ({
                  value: s,
                  label: t(`catalog.products.status.${s}`),
                }))}
                data-testid={CTID.editorStatus}
              />
            </Field>
            <fieldset className="flex flex-col gap-1">
              <legend className="pb-1 text-sm font-medium">
                {t("catalog.editor.channelsLabel")}
              </legend>
              {(["web", "pos"] as const).map((ch) => (
                <Checkbox
                  key={ch}
                  label={t(`catalog.products.channels.${ch}`)}
                  checked={d.channels.includes(ch)}
                  onChange={(e) =>
                    set(
                      "channels",
                      e.target.checked
                        ? [...new Set([...d.channels, ch])]
                        : d.channels.filter((c) => c !== ch),
                    )
                  }
                />
              ))}
              {errors.channels ? <p className="text-sm text-danger-fg">{errors.channels}</p> : null}
            </fieldset>
            <Field label={t("catalog.editor.purchaseLabel")}>
              <Select
                value={d.purchasable}
                onChange={(e) => set("purchasable", e.target.value as PurchasableMode)}
                options={(["buy", "coming_soon", "price_on_request", "external"] as const).map(
                  (m) => ({
                    value: m,
                    label: t(`catalog.editor.purchase.${m}`),
                  }),
                )}
              />
            </Field>
            {d.purchasable === "external" ? (
              <Field
                label={t("catalog.editor.externalUrlLabel")}
                help={t("catalog.editor.externalUrlHelp")}
              >
                <Input
                  type="url"
                  value={d.externalUrl}
                  onChange={(e) => set("externalUrl", e.target.value)}
                />
              </Field>
            ) : null}
          </Card>
          <Card title={t("catalog.editor.sections.organization")} id="organization">
            <fieldset className="flex flex-col gap-1">
              <legend className="pb-1 text-sm font-medium">
                {t("catalog.editor.categoriesLabel")}
              </legend>
              {categories.data?.data.length ? (
                categories.data.data.map((c) => (
                  <Checkbox
                    key={c.id}
                    label={
                      <span data-content="user">{`${"· ".repeat(Math.max(0, c.path.split("/").length - 2))}${c.name}`}</span>
                    }
                    checked={d.categoryIds.includes(c.id)}
                    onChange={(e) =>
                      set(
                        "categoryIds",
                        e.target.checked
                          ? [...d.categoryIds, c.id]
                          : d.categoryIds.filter((x) => x !== c.id),
                      )
                    }
                  />
                ))
              ) : (
                <p className="text-sm text-fg-secondary">{t("catalog.editor.categoriesEmpty")}</p>
              )}
            </fieldset>
            <Field label={t("catalog.editor.brandLabel")}>
              <Select
                value={d.brandId}
                onChange={(e) => set("brandId", e.target.value)}
                options={[
                  { value: "", label: t("catalog.editor.brandNone") },
                  ...(brands.data?.data ?? []).map((b) => ({ value: b.id, label: b.name })),
                ]}
              />
            </Field>
            <Field
              label={t("catalog.editor.tagsLabel")}
              help={t("catalog.editor.tagsHelp")}
              optional
            >
              <Input value={d.tags} onChange={(e) => set("tags", e.target.value)} userContent />
            </Field>
          </Card>
          <PhotosCard media={d.media} onChange={(m) => set("media", m)} disabled={!canWrite} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => goStep(2)}
              className="ady-bg-brand flex min-h-8 items-center rounded-xs px-3.5 text-xs font-medium text-on-primary"
            >
              {t("catalog.editor.steps.next", { step: t("catalog.editor.steps.variants") })}
            </button>
          </div>
        </section>
        <section
          id="product-step-2"
          aria-labelledby="product-step-2-h"
          className="flex scroll-mt-24 flex-col gap-4"
        >
          <h2 id="product-step-2-h" className="ady-sr-only">
            {t("catalog.editor.steps.variants")}
          </h2>
          <Card title={t("catalog.editor.sections.variants")} id="variants">
            <OptionsEditor axes={d.axes} onChange={(a) => set("axes", a)} />
            {errors.variants ? <Banner kind="error" title={errors.variants} /> : null}
            <VariantMatrix
              axes={axes}
              combos={combos}
              row={(o) => row(keyOf(axes, o))}
              setRow={(o, patch) => setRow(keyOf(axes, o), patch)}
              canCost={canCost}
            />
          </Card>
          <Card title={t("catalog.editor.sections.shipping")} id="shipping">
            <Switch
              label={t("catalog.editor.trackInventory")}
              description={t("catalog.editor.trackInventoryHelp")}
              checked={d.track}
              onCheckedChange={(v) => set("track", v)}
            />
            <Switch
              label={t("catalog.editor.allowBackorder")}
              checked={d.backorder}
              onCheckedChange={(v) => set("backorder", v)}
            />
            <Field label={t("catalog.editor.taxLabel")}>
              <Select
                value={d.tax}
                onChange={(e) => set("tax", e.target.value as Draft["tax"])}
                options={(["standard", "exempt", "zero"] as const).map((x) => ({
                  value: x,
                  label: t(`catalog.editor.tax.${x}`),
                }))}
              />
            </Field>
          </Card>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => goStep(3)}
              className="ady-bg-brand flex min-h-8 items-center rounded-xs px-3.5 text-xs font-medium text-on-primary"
            >
              {t("catalog.editor.steps.next", { step: t("catalog.editor.steps.custom") })}
            </button>
          </div>
        </section>
        <section
          id="product-step-3"
          aria-labelledby="product-step-3-h"
          className="flex scroll-mt-24 flex-col gap-4"
        >
          <h2 id="product-step-3-h" className="ady-sr-only">
            {t("catalog.editor.steps.custom")}
          </h2>
          <Card title={t("catalog.editor.sections.personalization")} id="personalization">
            <FieldsEditor fields={d.fields} onChange={(f) => set("fields", f)} />
          </Card>
          <Card title={t("catalog.editor.sections.seo")} id="seo">
            <p className="text-sm text-fg-secondary">{t("catalog.editor.seoHelp")}</p>
            <Field label={t("catalog.editor.seoTitle")} optional>
              <Input
                value={d.seoTitle}
                onChange={(e) => set("seoTitle", e.target.value)}
                maxGraphemes={70}
                userContent
              />
            </Field>
            <Field label={t("catalog.editor.seoDescription")} optional>
              <Textarea
                value={d.seoDescription}
                onChange={(e) => set("seoDescription", e.target.value)}
                rows={3}
              />
            </Field>
          </Card>
        </section>
      </fieldset>
      {initial ? (
        <Dialog
          open={deleting}
          onClose={() => setDeleting(false)}
          title={t("catalog.editor.deleteTitle", { title: initial.title })}
          description={t("catalog.editor.deleteBody")}
          confirm={{
            label: t("catalog.editor.delete"),
            variant: "danger",
            loading: remove.isPending,
            onClick: () => remove.mutate(),
          }}
        />
      ) : null}
    </CatalogPage>
  );
}

// ---------------------------------------------------------------- options + matrix

function OptionsEditor({
  axes,
  onChange,
}: {
  axes: AxisDraft[];
  onChange: (a: AxisDraft[]) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-md font-semibold">{t("catalog.variants.optionsTitle")}</h3>
        <p className="text-sm text-fg-secondary">{t("catalog.variants.optionsHelp")}</p>
      </div>
      {axes.map((a, i) => (
        <div key={i} className="grid gap-3 rounded-sm border border-subtle p-3 sm:grid-cols-3">
          <Field label={t("catalog.variants.optionName", { n: i + 1 })}>
            <Input
              value={a.name}
              onChange={(e) =>
                onChange(axes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
              }
              userContent
              data-testid={CTID.optionName(i)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label={t("catalog.variants.optionValues")}
              help={t("catalog.variants.optionValuesHelp")}
            >
              <Input
                value={a.values}
                onChange={(e) =>
                  onChange(axes.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)))
                }
                userContent
                data-testid={CTID.optionValues(i)}
              />
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(axes.filter((_, j) => j !== i))}
            >
              {t("catalog.variants.removeOption")}
            </Button>
          </div>
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          size="md"
          icon="plus"
          onClick={() => onChange([...axes, { name: "", values: "" }])}
          availability={
            axes.length >= 3
              ? { kind: "disabled", reason: t("catalog.variants.maxOptions") }
              : undefined
          }
          data-testid={CTID.addOption}
        >
          {t("catalog.variants.addOption")}
        </Button>
      </div>
    </div>
  );
}

function VariantMatrix({
  axes,
  combos,
  row,
  setRow,
  canCost,
}: {
  axes: Axis[];
  combos: Record<string, string>[];
  row: (o: Record<string, string>) => RowDraft;
  setRow: (o: Record<string, string>, patch: Partial<RowDraft>) => void;
  canCost: boolean;
}) {
  const count = combos.filter((o) => row(o).selected).length;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-md font-semibold">{t("catalog.variants.matrixTitle")}</h3>
          {axes.length ? (
            <p className="text-sm text-fg-secondary">{t("catalog.variants.matrixHelp")}</p>
          ) : null}
        </div>
        {axes.length ? (
          <span className="flex flex-wrap items-center gap-2 text-sm text-fg-secondary">
            {t("catalog.variants.count", { count, total: combos.length })}
            <Button
              variant="link"
              onClick={() => combos.forEach((o) => setRow(o, { selected: true }))}
            >
              {t("catalog.variants.selectAll")}
            </Button>
            <Button
              variant="link"
              onClick={() => combos.forEach((o) => setRow(o, { selected: false }))}
            >
              {t("catalog.variants.selectNone")}
            </Button>
          </span>
        ) : null}
      </div>
      <ul className="flex flex-col divide-y divide-subtle rounded-md border border-subtle">
        {combos.map((o, i) => {
          const r = row(o);
          const title = titleOf(axes, o);
          return (
            <li
              key={keyOf(axes, o)}
              className="flex flex-col gap-3 p-3"
              data-testid={CTID.variantRow}
              data-selected={r.selected}
            >
              {axes.length ? (
                <Checkbox
                  label={
                    <span className="font-medium" data-content="user">
                      {title}
                    </span>
                  }
                  aria-label={t("catalog.variants.include", { title })}
                  checked={r.selected}
                  onChange={(e) => setRow(o, { selected: e.target.checked })}
                />
              ) : null}
              {r.selected ? (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <Field label={t("catalog.variants.priceLabel")} required>
                    <MoneyInput
                      value={r.price}
                      onValueChange={(p) => setRow(o, { price: p })}
                      data-testid={CTID.variantPrice(i)}
                    />
                  </Field>
                  <Field label={t("catalog.variants.compareAtLabel")} optional>
                    <MoneyInput
                      value={r.compareAt}
                      onValueChange={(p) => setRow(o, { compareAt: p })}
                    />
                  </Field>
                  {canCost ? (
                    <Field
                      label={t("catalog.variants.costLabel")}
                      help={t("catalog.variants.costHelp")}
                      optional
                    >
                      <MoneyInput
                        value={r.cost}
                        onValueChange={(p) => setRow(o, { cost: p })}
                        data-testid={CTID.variantCost(i)}
                      />
                    </Field>
                  ) : null}
                  <Field label={t("catalog.variants.skuLabel")} optional>
                    <Input
                      value={r.sku}
                      onChange={(e) => setRow(o, { sku: e.target.value })}
                      autoCapitalize="characters"
                      spellCheck={false}
                    />
                  </Field>
                  <Field label={t("catalog.variants.barcodeLabel")} optional>
                    <Input
                      value={r.barcode}
                      onChange={(e) => setRow(o, { barcode: e.target.value })}
                      inputMode="numeric"
                      ledger
                    />
                  </Field>
                  <Field label={t("catalog.variants.weightLabel")} optional>
                    <Input
                      value={r.weight}
                      onChange={(e) => setRow(o, { weight: e.target.value.replace(/[^\d]/g, "") })}
                      inputMode="numeric"
                      ledger
                    />
                  </Field>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- personalisation

const FIELD_TYPES: FieldType[] = [
  "text",
  "textarea",
  "number",
  "select",
  "multi",
  "date",
  "slot",
  "image",
];

function FieldsEditor({
  fields,
  onChange,
}: {
  fields: FieldDraft[];
  onChange: (f: FieldDraft[]) => void;
}) {
  const upd = (i: number, patch: Partial<FieldDraft>) =>
    onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-fg-secondary">{t("catalog.customFields.help")}</p>
      {fields.length === 0 ? (
        <p className="text-sm text-fg-tertiary">{t("catalog.customFields.empty")}</p>
      ) : null}
      {fields.map((f, i) => (
        <div key={i} className="grid gap-3 rounded-sm border border-subtle p-3 sm:grid-cols-2">
          <Field label={t("catalog.customFields.labelLabel")}>
            <Input
              value={f.label}
              onChange={(e) => upd(i, { label: e.target.value })}
              userContent
            />
          </Field>
          <Field label={t("catalog.customFields.typeLabel")}>
            <Select
              value={f.type}
              onChange={(e) => upd(i, { type: e.target.value as FieldType })}
              options={FIELD_TYPES.map((ty) => ({
                value: ty,
                label: t(`catalog.customFields.types.${ty}`),
              }))}
            />
          </Field>
          {f.type === "select" || f.type === "multi" || f.type === "slot" ? (
            <div className="sm:col-span-2">
              <Field
                label={t("catalog.customFields.choicesLabel")}
                help={t("catalog.customFields.choicesHelp")}
              >
                <Input
                  value={f.choices}
                  onChange={(e) => upd(i, { choices: e.target.value })}
                  userContent
                />
              </Field>
            </div>
          ) : null}
          <Field label={t("catalog.customFields.surchargeLabel")} optional>
            <MoneyInput value={f.surcharge} onValueChange={(p) => upd(i, { surcharge: p })} />
          </Field>
          <div className="flex items-end justify-between gap-3">
            <Checkbox
              label={t("catalog.customFields.required")}
              checked={f.required}
              onChange={(e) => upd(i, { required: e.target.checked })}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(fields.filter((_, j) => j !== i))}
            >
              {t("catalog.customFields.remove")}
            </Button>
          </div>
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          size="md"
          icon="plus"
          onClick={() =>
            onChange([
              ...fields,
              { type: "text", label: "", required: false, surcharge: null, choices: "" },
            ])
          }
        >
          {t("catalog.customFields.add")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- photos

export type MediaRef = Draft["media"][number];

function PhotosCard({
  media,
  onChange,
  disabled,
}: {
  media: MediaRef[];
  onChange: (m: MediaRef[]) => void;
  disabled: boolean;
}) {
  const { orgId, storeId } = useStoreIds();
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [picking, setPicking] = useState(false);
  const latest = useRef(media);
  useEffect(() => {
    latest.current = media;
  }, [media]);

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = "";
    if (!files.length) return;
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (const [i, f] of files.entries()) {
      try {
        const asset = await uploadPhoto(orgId, storeId, f, crypto.randomUUID());
        ok++;
        latest.current = [
          ...latest.current,
          { asset_id: asset.id, alt: "", preview_path: asset.preview_path },
        ];
        onChange(latest.current);
      } catch (err) {
        const key =
          err instanceof UploadRejected
            ? err.reason === "type"
              ? "catalog.media.typeNotAllowed"
              : "catalog.media.tooLarge"
            : "catalog.media.uploadFailed";
        toast.show({
          tone: "error",
          title: t(key, { name: f.name }),
          ...(err instanceof ApiError ? { body: catalogError(err) } : {}),
        });
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setProgress(null);
    if (ok) toast.show({ tone: "success", title: t("catalog.media.uploaded", { count: ok }) });
    void qc.invalidateQueries({ queryKey: catalogKeys.media(orgId) });
  };

  return (
    <Card title={t("catalog.editor.sections.media")} id="media">
      {media.length ? (
        <ul
          className="grid grid-cols-3 gap-3 sm:grid-cols-4"
          aria-label={t("catalog.media.gridLabel")}
        >
          {media.map((m) => (
            <li key={m.asset_id} className="flex flex-col gap-1" data-testid={CTID.photoItem}>
              {m.preview_path ? (
                <img
                  src={m.preview_path}
                  alt={m.alt}
                  className="aspect-square w-full rounded-sm border border-subtle bg-sunken object-cover"
                />
              ) : (
                <span className="aspect-square w-full rounded-sm border border-subtle bg-sunken" />
              )}
              {!disabled ? (
                <Button
                  variant="link"
                  onClick={() => onChange(media.filter((x) => x.asset_id !== m.asset_id))}
                >
                  {t("catalog.media.remove")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          size="inline"
          headingLevel={3}
          title={t("catalog.media.emptyTitle")}
          body={t("catalog.media.emptyBody")}
        />
      )}
      {!disabled ? (
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            multiple
            hidden
            onChange={(e) => void onFiles(e)}
            data-testid={CTID.photoUpload}
          />
          <Button
            variant="secondary"
            icon="plus"
            loading={!!progress}
            loadingLabel={progress ? t("catalog.media.uploading", progress) : undefined}
            onClick={() => input.current?.click()}
          >
            {t("catalog.media.upload")}
          </Button>
          <Button variant="ghost" onClick={() => setPicking(true)}>
            {t("catalog.media.pickFromLibrary")}
          </Button>
        </div>
      ) : null}
      <LibraryPicker
        open={picking}
        onClose={() => setPicking(false)}
        exclude={media.map((m) => m.asset_id)}
        onPick={(picked) => {
          onChange([...media, ...picked]);
          setPicking(false);
        }}
      />
    </Card>
  );
}

export function LibraryPicker({
  open,
  onClose,
  exclude,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  exclude: string[];
  onPick: (m: MediaRef[]) => void;
}) {
  const { orgId } = useStoreIds();
  const lib = useMediaLibrary(orgId, open);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!open) setChosen(new Set());
  }, [open]);
  const items = (lib.data?.data ?? []).filter(
    // Photos only: product videos (1023) live in the library too.
    (a) => a.state === "ready" && a.purpose !== "product_video" && !exclude.includes(a.id),
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("catalog.media.pickTitle")}
      size="lg"
      confirm={{
        label: t("catalog.media.pickConfirm"),
        onClick: () =>
          onPick(
            items
              .filter((a) => chosen.has(a.id))
              .map((a) => ({ asset_id: a.id, alt: a.alt, preview_path: a.preview_path })),
          ),
      }}
    >
      {lib.isPending ? (
        <SkeletonText lines={3} />
      ) : lib.isError ? (
        <LoadError error={lib.error} onRetry={() => void lib.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          size="inline"
          headingLevel={3}
          title={t("catalog.media.emptyTitle")}
          body={t("catalog.media.emptyBody")}
        />
      ) : (
        <ul className="grid grid-cols-3 gap-3 pt-4 sm:grid-cols-4">
          {items.map((a) => (
            <li key={a.id}>
              <label className="flex cursor-pointer flex-col gap-1">
                {a.preview_path ? (
                  <img
                    src={a.preview_path}
                    alt={a.alt}
                    className="aspect-square w-full rounded-sm border border-subtle object-cover"
                  />
                ) : null}
                <Checkbox
                  label={<span className="ady-sr-only">{a.filename}</span>}
                  checked={chosen.has(a.id)}
                  onChange={(e) =>
                    setChosen((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(a.id);
                      else next.delete(a.id);
                      return next;
                    })
                  }
                />
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

const STEPS = ["general", "variants", "custom"] as const;

/** The tinted three-step header (D-038): numbered circles, "Step n" and the step name. */
function ProductSteps({ current, onPick }: { current: number; onPick: (n: number) => void }) {
  return (
    <nav aria-label={t("catalog.editor.steps.label")} className="max-w-4xl">
      <ol className="ady-bg-brand-tint flex flex-col gap-3 rounded-sm p-4 sm:flex-row sm:items-center">
        {STEPS.map((key, i) => {
          const n = i + 1;
          const active = n === current;
          return (
            <li key={key} className="flex flex-1 items-center gap-3">
              <button
                type="button"
                onClick={() => onPick(n)}
                aria-current={active ? "step" : undefined}
                className="flex items-center gap-3 text-start"
              >
                <span
                  className={cx(
                    "flex size-8 flex-none items-center justify-center rounded-full text-sm font-semibold",
                    active ? "ady-bg-brand text-on-primary" : "bg-surface text-fg-secondary",
                  )}
                >
                  {n}
                </span>
                <span className="flex flex-col">
                  <span
                    className={cx(
                      "text-xs font-bold uppercase",
                      active ? "ady-text-brand" : "text-fg",
                    )}
                  >
                    {t("catalog.editor.steps.step", { n })}
                  </span>
                  <span className="text-xs text-fg-tertiary">
                    {t(`catalog.editor.steps.${key}`)}
                  </span>
                </span>
              </button>
              {n < STEPS.length ? (
                <span className="hidden h-px flex-1 bg-line sm:block" aria-hidden="true" />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
